import { useCallback, useState } from 'react';

import pick from 'lodash/pick';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { useDefaultCatch } from '@atlassian/eoc-catch';
import { errorToastProps, successToastProps } from '@atlassian/eoc-common';
import { useFlags } from '@atlassian/eoc-global-context';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import { messages as commonMessages } from '../../common/messages';
import { PageModalType } from '../../common/types';
import { useActions as usePageActions } from '../../controllers/page-store';
import { type ScheduleEntity } from '../../services/endpoints/list/types';
import { endpoints as sectionEndpoints } from '../../services/endpoints/section';
import type { CreateScheduleParameters } from '../../services/endpoints/section/types';
import { getSchedulesResource } from '../../services/resources';
import { useTeamId } from '../use-team-id';

import { messages } from './messages';

const useCreateScheduleService = () => {
	const { update: updateSchedulesResource } = useResource(getSchedulesResource);
	const { openEditMode, closePageModal } = usePageActions();
	const flagApi = useFlags();
	const intlApi = useIntl();
	const teamId = useTeamId();
	const defaultCatch = useDefaultCatch();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isLoading, setIsLoading] = useState(false);

	const createSchedule = useCallback(
		async ({ name, timezone, description }: Omit<CreateScheduleParameters, 'teamId'>) => {
			try {
				setIsLoading(true);

				const response = await sectionEndpoints.createSchedule({
					name,
					timezone,
					description,
					teamId,
				});

				const event = createAnalyticsEvent({});
				fireTrackAnalytics(event, 'schedule created', response.id);

				const schedule = pick(response, [
					'id',
					'name',
					'description',
					'timezone',
					'enabled',
					'utcTimeOffsetMillis',
				]);

				updateSchedulesResource((prev) => {
					return {
						schedules: [...(prev?.schedules ?? []), schedule],
					};
				});

				flagApi.showFlag({
					...successToastProps(),
					title: intlApi.formatMessage(messages.createScheduleSuccessTitle),
					isAutoDismiss: true,
				});

				closePageModal(PageModalType.CreateSchedule);
				openEditMode({
					schedule,
					replace: true,
					id: [schedule.id, 'create-schedule-button'],
				});
			} catch (error) {
				defaultCatch<void>(
					{
						id: 'useCreateScheduleService-createSchedule',
						httpErrorHandler: (err) => {
							if (err.message) {
								flagApi.showFlag({
									...errorToastProps(),
									description: err.message,
									title: intlApi.formatMessage(commonMessages.error),
								});
							} else {
								flagApi.showFlag({
									...errorToastProps(),
									title: intlApi.formatMessage(messages.createScheduleErrorTitle),
								});
							}
						},
					},
					error,
				);

				throw error;
			} finally {
				setIsLoading(false);
			}
		},
		[
			teamId,
			updateSchedulesResource,
			flagApi,
			intlApi,
			closePageModal,
			openEditMode,
			defaultCatch,
			createAnalyticsEvent,
		],
	);

	return { createSchedule, isLoading };
};

const useCloneScheduleService = () => {
	const { update: updateSchedulesResource } = useResource(getSchedulesResource);
	const { openEditMode, closePageModal } = usePageActions();
	const flagApi = useFlags();
	const intlApi = useIntl();
	const teamId = useTeamId();
	const defaultCatch = useDefaultCatch();

	const [isLoading, setIsLoading] = useState(false);

	const cloneSchedule = useCallback(
		async ({
			id: scheduleId,
			name,
			enabled,
			timezone,
			description,
		}: Omit<ScheduleEntity, 'teamId' | 'utcTimeOffsetMillis'>) => {
			try {
				setIsLoading(true);

				const response = await sectionEndpoints.cloneSchedule({
					name,
					enabled,
					timezone,
					description,
					scheduleId,
					teamId,
				});

				// REFACTOR: Should we continue this usage elsewhere
				const schedule = pick(response, [
					'id',
					'name',
					'description',
					'timezone',
					'enabled',
					'utcTimeOffsetMillis',
				]);

				updateSchedulesResource((prev) => {
					return {
						schedules: [...(prev?.schedules ?? []), schedule],
					};
				});

				flagApi.showFlag({
					...successToastProps(),
					title: intlApi.formatMessage(messages.cloneScheduleSuccessTitle),
					isAutoDismiss: true,
				});

				closePageModal(PageModalType.CreateSchedule);
				openEditMode({
					schedule,
					replace: true,
					id: [schedule.id, 'create-schedule-button'],
				});
			} catch (error) {
				defaultCatch<void>(
					{
						id: 'useCloneScheduleService-cloneSchedule',
						httpErrorHandler: (err) => {
							if (err.message) {
								flagApi.showFlag({
									...errorToastProps(),
									description: err.message,
									title: intlApi.formatMessage(commonMessages.error),
								});
							} else {
								flagApi.showFlag({
									...errorToastProps(),
									title: intlApi.formatMessage(messages.cloneScheduleErrorTitle),
								});
							}
						},
					},
					error,
				);
			} finally {
				setIsLoading(false);
			}
		},
		[defaultCatch, flagApi, intlApi, openEditMode, closePageModal, teamId, updateSchedulesResource],
	);

	return { cloneSchedule, isLoading };
};

export { useCreateScheduleService, useCloneScheduleService };
